import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import PageHeader from '../components/common/PageHeader';
import { Plus, Trash2, GripVertical, Search } from 'lucide-react';
import { collection, addDoc, deleteDoc, doc, onSnapshot, query, setDoc, updateDoc, arrayUnion, arrayRemove, getDoc, getDocs, where, orderBy, limit } from 'firebase/firestore';
import { db, firebaseConfig } from '../firebase';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import Swal from 'sweetalert2';
import { generateSound } from '../hooks/useNotifications';
import { Volume2 } from 'lucide-react';
import { logAuditAction } from '../api/auditService';
import { OFFICIAL_ASSETS, saveBrandingAssets, uploadBrandingAsset } from '../api/brandingService';
import { useBranding } from '../hooks/useBranding';

const Configuracion = () => {
  const { currentUser } = useAuth();
  const branding = useBranding();
  const [uploadingBranding, setUploadingBranding] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [activeTab, setActiveTab] = useState(['ADMIN', 'GERENTE'].includes(currentUser?.role?.toUpperCase()) ? 'financiero' : 'preferencias');
  const [categorias, setCategorias] = useState([]);
  const [nuevaCatNombre, setNuevaCatNombre] = useState('');
  const [nuevaCatTipo, setNuevaCatTipo] = useState('ingreso');

  const [servicios, setServicios] = useState([]);
  const [nuevoServicio, setNuevoServicio] = useState('');
  const [nuevoServicioColor, setNuevoServicioColor] = useState('#6366f1');
  const [nuevoServicioMin, setNuevoServicioMin] = useState(5);
  const [nuevoServicioTipo, setNuevoServicioTipo] = useState('Cuenta Completa');
  const [usuarios, setUsuarios] = useState([]);

  // Empresa Settings
  const [configEmpresa, setConfigEmpresa] = useState({ reserva_porcentaje: 0 });

  // Socios State
  const [socios, setSocios] = useState([]);
  const [nuevoSocio, setNuevoSocio] = useState({ nombre: '', porcentaje: 50, estado: 'activo' });

  // Rutinas State
  const [rutinas, setRutinas] = useState([]);
  const [nuevaRutinaDep, setNuevaRutinaDep] = useState('Gerencia');
  const [nuevaRutinaTarea, setNuevaRutinaTarea] = useState('');


  // Users Modal State
  const [showUserModal, setShowUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'ATENCIÓN AL CLIENTE', password: '' });

  // Preferences State
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [soundType, setSoundType] = useState('pop');

  // Config Operativa State
  const [configOperativa, setConfigOperativa] = useState({ inicio_jornada: '10:00', cierre_jornada: '23:00' });
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSearch, setAuditSearch] = useState('');

  React.useEffect(() => {
    const qCat = query(collection(db, 'config_categorias'));
    const unsubCat = onSnapshot(qCat, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      setCategorias(data);
    }, (error) => console.error("Error fetching categorias: ", error));

    const qServ = query(collection(db, 'config_servicios'));
    const unsubServ = onSnapshot(qServ, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      setServicios(data);
    });

    const qUsers = query(collection(db, 'users'));
    const unsubUsers = onSnapshot(qUsers, (snapshot) => {
      setUsuarios(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qSocios = query(collection(db, 'config_socios'));
    const unsubSocios = onSnapshot(qSocios, (snapshot) => {
      setSocios(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qRutinas = query(collection(db, 'config_rutinas'));
    const unsubRutinas = onSnapshot(qRutinas, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      setRutinas(data);
    });

    const initATCRutinas = async () => {
      try {
        const q = query(collection(db, 'config_rutinas'), where('departamento', '==', 'Atención al Cliente'));
        const snap = await getDocs(q);
        if (snap.empty) {
          const preATC = [
            "10:00 AM — Cambiar la Tasa del dólar.",
            "Resolver solicitudes pendientes del día anterior.",
            "01:00 PM — Cambiar la Tasa del dólar.",
            "03:00 PM — Revisión de chats para asuntos pendientes.",
            "Enviar los mensajes de cobro.",
            "Revisión de reportes.",
            "09:00 PM — Revisión de chats para asuntos pendientes.",
            "Cambio de estado en Chat Hoprin+ a 'Ausente'."
          ];
          
          for (let i = 0; i < preATC.length; i++) {
            await addDoc(collection(db, 'config_rutinas'), {
              departamento: 'Atención al Cliente',
              tarea: preATC[i],
              orden: i + 1
            });
          }
        }
      } catch (err) {
        console.error("Error al inicializar tareas ATC: ", err);
      }
    };
    initATCRutinas();


    const unsubEmpresa = onSnapshot(doc(db, 'config_empresa', 'main'), (docSnap) => {
      if (docSnap.exists()) setConfigEmpresa(docSnap.data());
    });

    const unsubOperativa = onSnapshot(doc(db, 'config_operativa', 'main'), (docSnap) => {
      if (docSnap.exists()) setConfigOperativa(prev => ({ ...prev, ...docSnap.data() }));
    });

    const qAudit = query(collection(db, 'logs_auditoria'), orderBy('timestamp', 'desc'), limit(150));
    const unsubAudit = onSnapshot(qAudit, (snapshot) => {
        setAuditLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    if (currentUser?.uid) {
        getDoc(doc(db, 'users', currentUser.uid)).then(d => {
            if (d.exists() && d.data().preferences) {
                setSoundEnabled(d.data().preferences.soundEnabled ?? true);
                setSoundType(d.data().preferences.soundType || 'pop');
            }
        });
    }

    return () => { unsubCat(); unsubServ(); unsubUsers(); unsubSocios(); unsubRutinas(); unsubEmpresa(); unsubOperativa(); unsubAudit(); };
  }, [currentUser]);

  const handleSaveEmpresa = async (e) => {
    e.preventDefault();
    try {
      await setDoc(doc(db, 'config_empresa', 'main'), configEmpresa, { merge: true });
      logAuditAction({
          userId: currentUser?.uid || currentUser?.id,
          userName: currentUser?.name || currentUser?.email,
          modulo: 'Configuración',
          accion: 'Edición Configuración Empresa',
          descripcion: `Cambio en reserva: ${configEmpresa.reserva_porcentaje}%, Umbral: ${configEmpresa.umbral_gasto_saludable}%`
      });
      Swal.fire({ title: 'Guardado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo guardar la configuración', 'error');
    }
  };

  const handleSaveJornada = async (e) => {
    e.preventDefault();
    try {
      await setDoc(doc(db, 'config_operativa', 'main'), configOperativa, { merge: true });
      logAuditAction({
          userId: currentUser?.uid || currentUser?.id,
          userName: currentUser?.name || currentUser?.email,
          modulo: 'Configuración',
          accion: 'Edición Horario Jornada',
          descripcion: `Inicio: ${configOperativa.inicio_jornada}, Cierre: ${configOperativa.cierre_jornada}`
      });
      Swal.fire({ title: 'Jornada Actualizada', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo guardar la jornada', 'error');
    }
  };

  const handleAddCategoria = async (e) => {
    e.preventDefault();
    if (!nuevaCatNombre.trim()) return Swal.fire('Error', 'El nombre es obligatorio', 'error');
    
    try {
      await addDoc(collection(db, 'config_categorias'), {
        nombre: nuevaCatNombre.trim(),
        tipo: nuevaCatTipo,
        subcategorias: [],
        creadoPor: currentUser.uid || currentUser.email
      });
      logAuditAction({
          userId: currentUser?.uid || currentUser?.id,
          userName: currentUser?.name || currentUser?.email,
          modulo: 'Configuración',
          accion: 'Creación de Categoría',
          descripcion: `Creada categoría ${nuevaCatNombre} (${nuevaCatTipo})`
      });
      setNuevaCatNombre('');
      Swal.fire({ title: 'Añadida', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo añadir', 'error');
    }
  };

  const handleDeleteCategoria = async (id) => {
    try {
      await deleteDoc(doc(db, 'config_categorias', id));
      Swal.fire({ title: 'Eliminada', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar', 'error');
    }
  };

  const handleAddSubcategoria = async (catId, newSub) => {
    if (!newSub || !newSub.trim()) return;
    try {
      await updateDoc(doc(db, 'config_categorias', catId), {
        subcategorias: arrayUnion(newSub.trim())
      });
    } catch (error) {
      Swal.fire('Error', 'No se pudo añadir la subcategoría', 'error');
    }
  };

  const handleRemoveSubcategoria = async (catId, subToRemove) => {
    try {
      await updateDoc(doc(db, 'config_categorias', catId), {
        subcategorias: arrayRemove(subToRemove)
      });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar la subcategoría', 'error');
    }
  };

  const handleAddServicio = async (e) => {
    e.preventDefault();
    if (!nuevoServicio.trim()) return Swal.fire('Error', 'El nombre es obligatorio', 'error');
    
    try {
      await addDoc(collection(db, 'config_servicios'), {
        nombre: nuevoServicio.trim(),
        color: nuevoServicioColor,
        punto_de_reorden: Number(nuevoServicioMin),
        tipo_producto: nuevoServicioTipo
      });
      logAuditAction({
          userId: currentUser?.uid || currentUser?.id,
          userName: currentUser?.name || currentUser?.email,
          modulo: 'Configuración',
          accion: 'Creación de Servicio',
          descripcion: `Creado servicio ${nuevoServicio}`
      });
      setNuevoServicio('');
      setNuevoServicioColor('#6366f1');
      setNuevoServicioMin(5);
      setNuevoServicioTipo('Cuenta Completa');
      Swal.fire({ title: 'Añadido', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo añadir', 'error');
    }
  };

  const handleDeleteServicio = async (id) => {
    try {
      await deleteDoc(doc(db, 'config_servicios', id));
      logAuditAction({
          userId: currentUser?.uid || currentUser?.id,
          userName: currentUser?.name || currentUser?.email,
          modulo: 'Configuración',
          accion: 'Eliminación de Servicio',
          descripcion: `Eliminado servicio ID: ${id}`
      });
      Swal.fire({ title: 'Eliminado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar', 'error');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      import('firebase/firestore').then(({ updateDoc }) => {
        updateDoc(doc(db, 'users', userId), { role: newRole });
        Swal.fire({ title: 'Rol actualizado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
      });
    } catch (error) {
      Swal.fire('Error', 'No se pudo actualizar el rol', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Seguro que deseas eliminar a este usuario? Esto solo lo borra de la base de datos local.')) return;
    try {
      await deleteDoc(doc(db, 'users', userId));
      Swal.fire({ title: 'Eliminado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar el usuario', 'error');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) return Swal.fire('Error', 'Nombre, correo y contraseña son obligatorios', 'error');
    
    let secondaryApp;
    try {
      secondaryApp = initializeApp(firebaseConfig, "Secondary");
      const secondaryAuth = getAuth(secondaryApp);

      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newUser.email, newUser.password);
      const newUserId = userCredential.user.uid;

      await setDoc(doc(db, 'users', newUserId), {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        tempPassword: newUser.password // Just stored for reference
      });

      setShowUserModal(false);
      setNewUser({ name: '', email: '', role: 'ATENCIÓN AL CLIENTE', password: '' });
      Swal.fire('¡Éxito!', 'Usuario creado con éxito en el sistema. El empleado ya puede iniciar sesión.', 'success');
    } catch (error) {
      console.error("Error al crear usuario:", error);
      Swal.fire('Error', 'No se pudo crear el usuario: ' + error.message, 'error');
    } finally {
      if (secondaryApp) {
        await deleteApp(secondaryApp);
      }
    }
  };

  const handleAddSocio = async (e) => {
    e.preventDefault();
    if (!nuevoSocio.nombre) return Swal.fire('Error', 'El nombre es obligatorio', 'error');
    try {
      await addDoc(collection(db, 'config_socios'), {
        nombre: nuevoSocio.nombre,
        porcentaje_participacion: Number(nuevoSocio.porcentaje),
        estado: nuevoSocio.estado
      });
      setNuevoSocio({ nombre: '', porcentaje: 50, estado: 'activo' });
      Swal.fire({ title: 'Añadido', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo añadir socio', 'error');
    }
  };

  const handleDeleteSocio = async (id) => {
    try {
      await deleteDoc(doc(db, 'config_socios', id));
      Swal.fire({ title: 'Eliminado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar socio', 'error');
    }
  };

  const handleAddRutina = async (e) => {
    e.preventDefault();
    if (!nuevaRutinaTarea.trim()) return Swal.fire('Error', 'La tarea es obligatoria', 'error');
    try {
      const depTasks = rutinas.filter(r => r.departamento === nuevaRutinaDep);
      const maxOrden = depTasks.reduce((max, r) => Math.max(max, r.orden || 0), 0);
      
      await addDoc(collection(db, 'config_rutinas'), {
        departamento: nuevaRutinaDep,
        tarea: nuevaRutinaTarea.trim(),
        orden: maxOrden + 1
      });
      setNuevaRutinaTarea('');
      Swal.fire({ title: 'Añadida', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo añadir rutina', 'error');
    }
  };


  const handleDeleteRutina = async (id) => {
    try {
      await deleteDoc(doc(db, 'config_rutinas', id));
      Swal.fire({ title: 'Eliminada', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar rutina', 'error');
    }
  };





  const [isResetting, setIsResetting] = useState(false);

  const handleResetOperaciones = async () => {
    const result = await Swal.fire({
      title: '¿Estás completamente seguro?',
      text: "Esta acción eliminará todos los documentos de: inventario_cuentas, finanzas_gastos, asistencia_logs, notas, rutinas_diarias e insumos_stock. NO se podrá revertir.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, ¡Limpiar Datos!',
      cancelButtonText: 'Cancelar',
      background: '#1e293b',
      color: '#fff'
    });

    if (result.isConfirmed) {
      setIsResetting(true);
      try {
        const coleccionesAEliminar = [
          'inventario_cuentas', 
          'finanzas_gastos', 
          'asistencia_logs', 
          'notas', 
          'rutinas_diarias', 
          'insumos_stock'
        ];

        for (const colName of coleccionesAEliminar) {
          const q = query(collection(db, colName));
          const snapshot = await getDocs(q);
          for (const document of snapshot.docs) {
            await deleteDoc(doc(db, colName, document.id));
          }
        }

        Swal.fire({
          title: 'Sistema Reseteado',
          text: 'Las operaciones se han limpiado correctamente.',
          icon: 'success',
          background: '#1e293b',
          color: '#fff'
        });
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Hubo un error limpiando las operaciones.', 'error');
      } finally {
        setIsResetting(false);
      }
    }
  };

  const handleSavePreferences = async (e) => {
      e.preventDefault();
      try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
              preferences: { soundEnabled, soundType }
          });
          Swal.fire({ title: 'Preferencias Guardadas', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
      } catch (error) {
          Swal.fire('Error', 'No se pudieron guardar las preferencias', 'error');
      }
  };

  const brandingAssets = [
    { kind: 'logo', name: 'Logo Principal (Login y Sidebar)', badge: 'PNG · 512 × 512 px · Máx 2 MB', width: 512, height: 512, maxBytes: 2 * 1024 * 1024, url: branding.logoUrl || OFFICIAL_ASSETS.logo },
    { kind: 'favicon', name: 'Favicon (Pestaña del Navegador)', badge: 'PNG / ICO · 32 × 32 px · Máx 500 KB', width: 32, height: 32, maxBytes: 500 * 1024, url: branding.faviconUrl || OFFICIAL_ASSETS.favicon },
    { kind: 'pwa192', name: 'Icono PWA Móvil (Android / iOS)', badge: 'PNG · 192 × 192 px · Máx 1 MB', width: 192, height: 192, maxBytes: 1024 * 1024, url: branding.pwa192Url || OFFICIAL_ASSETS.pwa192 },
    { kind: 'pwa512', name: 'Icono PWA Alta Resolución (Splash Screen)', badge: 'PNG · 512 × 512 px · Máx 2 MB', width: 512, height: 512, maxBytes: 2 * 1024 * 1024, url: branding.pwa512Url || OFFICIAL_ASSETS.pwa512 },
    { kind: 'maskable', name: 'Icono PWA Adaptable (Android Maskable)', badge: 'PNG · 512 × 512 px · Máx 2 MB · Margen seguro 20%', width: 512, height: 512, maxBytes: 2 * 1024 * 1024, maskable: true, url: branding.maskableUrl || OFFICIAL_ASSETS.maskable },
    { kind: 'ogImage', name: 'Imagen al Compartir Enlace (Open Graph / WhatsApp)', badge: 'PNG / JPG · 1200 × 630 px · Máx 2 MB', width: 1200, height: 630, maxBytes: 2 * 1024 * 1024, url: branding.ogImageUrl || OFFICIAL_ASSETS.ogImage }
  ];

  const loadImage = file => new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => { URL.revokeObjectURL(objectUrl); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('No se pudo leer la imagen seleccionada.')); };
    image.src = objectUrl;
  });

  const createAssetBlob = async (file, asset) => {
    const image = await loadImage(file);
    const canvas = document.createElement('canvas');
    canvas.width = asset.width; canvas.height = asset.height;
    const context = canvas.getContext('2d');
    context.imageSmoothingEnabled = true; context.imageSmoothingQuality = 'high';
    if (asset.maskable) context.fillStyle = '#180836';
    else if (asset.kind === 'ogImage') context.fillStyle = '#180836';
    if (asset.maskable || asset.kind === 'ogImage') context.fillRect(0, 0, asset.width, asset.height);
    const safe = asset.maskable ? 0.6 : 1;
    const scale = Math.min((asset.width * safe) / image.width, (asset.height * safe) / image.height);
    const drawWidth = image.width * scale; const drawHeight = image.height * scale;
    context.drawImage(image, (asset.width - drawWidth) / 2, (asset.height - drawHeight) / 2, drawWidth, drawHeight);
    return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('No se pudo procesar la imagen.')), 'image/png', 0.92));
  };

  const blobToDataUrl = blob => new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = () => reject(new Error('No se pudo guardar la imagen.')); reader.readAsDataURL(blob);
  });

  const handleBrandingUpload = async (kind, file) => {
    if (!file) return;
    const asset = brandingAssets.find(item => item.kind === kind);
    if (!asset) return;
    if (!file.type.startsWith('image/') || file.size > asset.maxBytes) {
      Swal.fire('Archivo no válido', `Selecciona una imagen de hasta ${Math.round(asset.maxBytes / 1024)} KB.`, 'error'); return;
    }
    setUploadingBranding(kind);
    try {
      const blob = await createAssetBlob(file, asset);
      const dataUrl = await blobToDataUrl(blob);
      try { await uploadBrandingAsset(kind, blob, currentUser?.uid); }
      catch (storageError) { await saveBrandingAssets({ [kind]: dataUrl }, currentUser?.uid); }
      await logAuditAction({ userId: currentUser?.uid, userName: currentUser?.name || currentUser?.email, modulo: 'Configuración', accion: `Actualización de ${asset.name}`, descripcion: `Archivo: ${file.name}` });
      setUploadMessage('✓ Guardado y aplicado en tiempo real');
    } catch (error) { Swal.fire('Error', error.message || 'No se pudo actualizar la imagen.', 'error'); }
    finally { setUploadingBranding(''); }
  };

  const handleMasterLogo = async file => {
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 2 * 1024 * 1024) { Swal.fire('Archivo no válido', 'El Logo Maestro debe ser una imagen de hasta 2 MB.', 'error'); return; }
    setUploadingBranding('master');
    try {
      const variants = await Promise.all(brandingAssets.map(async asset => [asset.kind, await blobToDataUrl(await createAssetBlob(file, asset))]));
      await saveBrandingAssets(Object.fromEntries(variants), currentUser?.uid);
      setUploadMessage('✓ Guardado y aplicado en tiempo real');
    } catch (error) { Swal.fire('Error', error.message || 'No se pudieron generar los activos.', 'error'); }
    finally { setUploadingBranding(''); }
  };

  const downloadAsset = async asset => {
    try {
      const response = await fetch(asset.url); const blob = await response.blob();
      const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `${asset.kind}.png`; link.click(); URL.revokeObjectURL(link.href);
    } catch { window.open(asset.url, '_blank', 'noopener,noreferrer'); }
  };

  if (!currentUser) {
    return <div className="min-h-screen text-white p-8 flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen text-slate-100">
      <PageHeader title="Configuración" subtitle="Centro de control del sistema" />

      <div className="flex gap-4 border-b border-slate-800 mb-6 mt-6 overflow-x-auto">
        {['preferencias', 'identidad', 'usuarios', 'jornada', 'financiero', 'servicios', 'socios', 'rutinas', 'auditoria'].map((tab) => {
          if (!['ADMIN', 'GERENTE'].includes(currentUser?.role?.toUpperCase()) && tab !== 'preferencias') {return null;}
          return (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-2 font-medium transition-colors relative whitespace-nowrap ${activeTab === tab ? 'text-emerald-500' : 'text-slate-400 hover:text-slate-200'
              }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {activeTab === tab && (
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-500 rounded-t-full"></div>
            )}
          </button>
          );
        })}
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 min-h-[400px]">
        {activeTab === 'preferencias' && (
            <div className="space-y-6 animate-in fade-in duration-300 max-w-2xl">
                <div>
                    <h3 className="text-lg font-bold text-white mb-2">Preferencias de Notificación</h3>
                    <p className="text-slate-400 text-sm">Personaliza cómo deseas recibir alertas y avisos del sistema.</p>
                </div>
                <form onSubmit={handleSavePreferences} className="bg-slate-900/50 p-6 rounded-xl border border-slate-700 space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-white font-medium">Sonido de Notificaciones</h4>
                            <p className="text-slate-400 text-sm">Reproducir un sonido cuando llegue una alerta.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" checked={soundEnabled} onChange={e => setSoundEnabled(e.target.checked)} />
                            <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                    </div>
                    {soundEnabled && (
                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-slate-400">Tono de Alerta</label>
                        <div className="flex gap-4 items-center">
                            <select value={soundType} onChange={e => setSoundType(e.target.value)} className="w-full md:w-64 bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500">
                                <option value="pop">Pop (Discreto)</option>
                                <option value="beep">Beep (Clásico)</option>
                                <option value="chime">Chime (Campana)</option>
                                <option value="crystal">Crystal (Suave)</option>
                                <option value="alert">Alert (Urgente)</option>
                            </select>
                            <button type="button" onClick={() => generateSound(soundType)} className="p-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors" title="Probar Sonido">
                                <Volume2 size={20} />
                            </button>
                        </div>
                    </div>
                    )}
                    <button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                        Guardar Preferencias
                    </button>
                </form>

                {/* FUNCIÓN DE RESET DE OPERACIONES (ADMIN ÚNICAMENTE) */}
                {(currentUser?.role?.toUpperCase().includes('ADMIN') || currentUser?.role === 'Administrador') && (
                    <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-xl space-y-3 mt-8">
                        <h4 className="text-red-400 font-bold flex items-center gap-2">
                            ⚠️ Zona de Peligro: Limpieza de Datos
                        </h4>
                        <p className="text-slate-400 text-sm">
                            Esta función está diseñada para preparar el sistema para el lanzamiento oficial. Limpiará todo el historial técnico y comercial sin afectar usuarios o catálogos de servicios.
                        </p>
                        <button 
                            onClick={handleResetOperaciones} 
                            disabled={isResetting}
                            className="bg-red-600 hover:bg-red-500 text-white font-bold px-6 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                        >
                            {isResetting ? 'Limpiando Datos...' : '⚠️ Limpiar Datos Operativos para Lanzamiento'}
                        </button>
                    </div>
                )}
            </div>
        )}
        {activeTab === 'identidad' && (
          <div className="space-y-6 animate-in fade-in duration-300 max-w-6xl">
            <div><h3 className="text-lg font-bold text-white mb-2">Identidad visual</h3><p className="text-slate-400 text-sm">Cada imagen se redimensiona localmente con alta calidad. Si Storage no responde en 10 segundos, se guarda automáticamente en Firestore y en este navegador.</p></div>
            <label className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-5 py-3 rounded-xl cursor-pointer transition-colors disabled:opacity-60"><input type="file" accept="image/*" className="hidden" disabled={uploadingBranding === 'master'} onChange={event => handleMasterLogo(event.target.files?.[0])}/>{uploadingBranding === 'master' ? 'Generando los 6 tamaños…' : 'Subir Logo Maestro (Autogenerar los 6 tamaños)'}</label>
            {uploadMessage && <p className="text-sm font-medium text-emerald-300">{uploadMessage}</p>}
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {brandingAssets.map(asset => <article key={asset.kind} className="bg-slate-900/70 p-5 rounded-xl border border-slate-700"><h4 className="text-white font-semibold mb-2">{asset.name}</h4><span className="inline-block text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-1 mb-4">{asset.badge}</span><div className="h-40 bg-[#161b22] rounded-lg flex items-center justify-center p-3 mb-4"><img src={asset.url} alt={`Vista previa: ${asset.name}`} className="max-w-full max-h-full object-contain"/></div><div className="flex flex-col gap-2"><label className="text-center rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium px-3 py-2 cursor-pointer"><input type="file" accept="image/*" className="hidden" disabled={Boolean(uploadingBranding)} onChange={event => handleBrandingUpload(asset.kind, event.target.files?.[0])}/>{uploadingBranding === asset.kind ? 'Subiendo…' : 'Seleccionar archivo'}</label><button type="button" onClick={() => downloadAsset(asset)} className="rounded-lg border border-slate-600 hover:border-emerald-500 text-slate-200 text-sm font-medium px-3 py-2">Descargar actual</button></div></article>)}
            </div>
          </div>
        )}
        {activeTab === 'jornada' && (
          <div className="space-y-6 animate-in fade-in duration-300 max-w-2xl">
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Configuración Global de Jornada</h3>
              <p className="text-slate-400 text-sm">Define el rango horario operativo de la empresa. Los selectores de horario de empleados quedarán limitados a estos valores.</p>
            </div>
            <form onSubmit={handleSaveJornada} className="bg-slate-900/50 p-6 rounded-xl border border-slate-700 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Inicio de Jornada</label>
                  <input
                    type="time"
                    value={configOperativa.inicio_jornada}
                    onChange={e => setConfigOperativa(prev => ({ ...prev, inicio_jornada: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white text-lg font-mono focus:outline-none focus:border-emerald-500 [color-scheme:dark]"
                  />
                  <p className="text-xs text-slate-500 mt-1">Hora mínima disponible en los turnos</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Cierre de Jornada</label>
                  <input
                    type="time"
                    value={configOperativa.cierre_jornada}
                    onChange={e => setConfigOperativa(prev => ({ ...prev, cierre_jornada: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white text-lg font-mono focus:outline-none focus:border-emerald-500 [color-scheme:dark]"
                  />
                  <p className="text-xs text-slate-500 mt-1">Hora máxima disponible en los turnos</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/30">
                <span className="text-3xl">🕐</span>
                <div>
                  <p className="text-white font-medium">Jornada Activa: <span className="font-mono text-emerald-400">{configOperativa.inicio_jornada} AM</span> a <span className="font-mono text-amber-400">{configOperativa.cierre_jornada}</span></p>
                  <p className="text-slate-400 text-sm">El detector de cobertura evalúa este rango completo.</p>
                </div>
              </div>
              <button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                Guardar Jornada
              </button>
            </form>
          </div>
        )}
        {activeTab === 'usuarios' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Gestión de Usuarios</h3>
                <p className="text-slate-400 text-sm">Administra los roles y permisos de los usuarios del sistema.</p>
              </div>
              <button onClick={() => setShowUserModal(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors flex items-center gap-2">
                <Plus size={16} /> Nuevo Usuario
              </button>
            </div>
            
            <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
              <div className="overflow-x-auto whitespace-nowrap">
                <table className="w-full text-sm text-left text-slate-300">
                <thead className="bg-slate-900 text-slate-400 uppercase text-xs">
                  <tr>
                    <th className="px-6 py-3">Nombre</th>
                    <th className="px-6 py-3">Correo</th>
                    <th className="px-6 py-3">Rol</th>
                    <th className="px-6 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {usuarios.map(user => (
                    <tr key={user.id} className="hover:bg-slate-800/50">
                      <td className="px-6 py-4">{user.name || user.displayName || 'Sin Nombre'}</td>
                      <td className="px-6 py-4">{user.email}</td>
                      <td className="px-6 py-4">
                        <select 
                          value={user.role || 'OPERATIVO'} 
                          onChange={(e) => handleChangeRole(user.id, e.target.value)}
                          className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-white focus:outline-none focus:border-emerald-500"
                        >
                          <option value="ADMIN">ADMIN</option>
                          <option value="GERENTE">GERENTE</option>
                          <option value="ATENCIÓN AL CLIENTE">ATENCIÓN AL CLIENTE</option>
                          <option value="GESTIÓN DE CUENTAS">GESTIÓN DE CUENTAS</option>
                          <option value="MARKETING">MARKETING</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => handleDeleteUser(user.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                  {usuarios.length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-slate-500">No hay usuarios registrados</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
          </div>
        )}
        {activeTab === 'financiero' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* FONDO DE RESERVA */}
            <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-2">Fondo de Reserva Empresarial</h3>
              <p className="text-slate-400 text-sm mb-4">Define el porcentaje de la utilidad neta que se destinará al fondo de reserva de la empresa.</p>
              
              <form onSubmit={handleSaveEmpresa} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Porcentaje de Reserva (%)</label>
                  <input 
                    type="number" min="0" max="100" step="0.1"
                    value={configEmpresa.reserva_porcentaje || 0} 
                    onChange={(e) => setConfigEmpresa({...configEmpresa, reserva_porcentaje: Number(e.target.value)})} 
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1 font-semibold">Umbral de Gasto Saludable (%)</label>
                  <input 
                    type="number" min="0" max="100" step="1"
                    value={configEmpresa.umbral_gasto_saludable !== undefined ? configEmpresa.umbral_gasto_saludable : 60} 
                    onChange={(e) => setConfigEmpresa({...configEmpresa, umbral_gasto_saludable: Number(e.target.value)})} 
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500 font-bold" 
                  />
                </div>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-medium transition-colors font-bold h-[42px]">
                  Guardar Configuración
                </button>
              </form>
            </div>

            <div>
              <h3 className="text-lg font-bold text-white mb-2">Gestión de Categorías Financieras</h3>
              <p className="text-slate-400 text-sm mb-6">Administra las categorías que aparecerán en los formularios de registro de ingresos y gastos.</p>
              
              <form onSubmit={handleAddCategoria} className="flex flex-col md:flex-row gap-4 items-end bg-slate-900 p-4 rounded-xl border border-slate-700">
                <div className="flex-1 w-full">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Nombre de la Categoría</label>
                  <input type="text" value={nuevaCatNombre} onChange={(e) => setNuevaCatNombre(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" placeholder="Ej: Publicidad, Zelle, etc." />
                </div>
                <div className="w-full md:w-48">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Tipo</label>
                  <select value={nuevaCatTipo} onChange={(e) => setNuevaCatTipo(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500">
                    <option value="ingreso">Ingreso</option>
                    <option value="gasto">Gasto</option>
                  </select>
                </div>
                <button type="submit" className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                  <Plus size={18} /> Añadir
                </button>
              </form>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Lista Ingresos */}
              <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 bg-emerald-500/10 border-b border-slate-700">
                  <h4 className="font-bold text-emerald-400">Categorías de Ingreso</h4>
                </div>
                <ul className="divide-y divide-slate-700 max-h-64 overflow-y-auto">
                  {categorias.filter(c => c.tipo === 'ingreso').length === 0 ? (
                    <li className="p-4 text-sm text-slate-500 text-center">No hay categorías registradas</li>
                  ) : (
                    categorias.filter(c => c.tipo === 'ingreso').map(c => (
                      <li key={c.id} className="p-3 flex flex-col gap-2 hover:bg-slate-800/50">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-300 font-bold">{c.nombre}</span>
                          <button onClick={() => handleDeleteCategoria(c.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={16} /></button>
                        </div>
                        <div className="pl-4 mt-1 border-l border-slate-700">
                           <div className="flex flex-wrap gap-2 mb-2">
                             {(c.subcategorias || []).map((sub, idx) => (
                                <span key={idx} className="bg-slate-700 text-xs px-2 py-1 rounded flex items-center gap-1 text-slate-300">
                                   {sub}
                                   <button onClick={() => handleRemoveSubcategoria(c.id, sub)} className="text-slate-400 hover:text-red-400 ml-1">×</button>
                                </span>
                             ))}
                           </div>
                           <div className="flex gap-2">
                              <input 
                                type="text" 
                                placeholder="Añadir subcategoría..."
                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-emerald-500"
                                onKeyDown={(e) => {
                                   if(e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddSubcategoria(c.id, e.target.value);
                                      e.target.value = '';
                                   }
                                }}
                              />
                              <button 
                                 type="button"
                                 className="bg-slate-700 hover:bg-slate-600 px-2 py-1 rounded text-xs text-white"
                                 onClick={(e) => {
                                    const input = e.currentTarget.previousSibling;
                                    handleAddSubcategoria(c.id, input.value);
                                    input.value = '';
                                 }}
                              >
                                 +
                              </button>
                           </div>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              {/* Lista Gastos */}
              <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 bg-red-500/10 border-b border-slate-700">
                  <h4 className="font-bold text-red-400">Categorías de Gasto</h4>
                </div>
                <ul className="divide-y divide-slate-700 max-h-64 overflow-y-auto">
                  {categorias.filter(c => c.tipo === 'gasto').length === 0 ? (
                    <li className="p-4 text-sm text-slate-500 text-center">No hay categorías registradas</li>
                  ) : (
                    categorias.filter(c => c.tipo === 'gasto').map(c => (
                      <li key={c.id} className="p-3 flex flex-col gap-2 hover:bg-slate-800/50">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-300 font-bold">{c.nombre}</span>
                          <button onClick={() => handleDeleteCategoria(c.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={16} /></button>
                        </div>
                        <div className="pl-4 mt-1 border-l border-slate-700">
                           <div className="flex flex-wrap gap-2 mb-2">
                             {(c.subcategorias || []).map((sub, idx) => (
                                <span key={idx} className="bg-slate-700 text-xs px-2 py-1 rounded flex items-center gap-1 text-slate-300">
                                   {sub}
                                   <button onClick={() => handleRemoveSubcategoria(c.id, sub)} className="text-slate-400 hover:text-red-400 ml-1">×</button>
                                </span>
                             ))}
                           </div>
                           <div className="flex gap-2">
                              <input 
                                type="text" 
                                placeholder="Añadir subcategoría..."
                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-red-500"
                                onKeyDown={(e) => {
                                   if(e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddSubcategoria(c.id, e.target.value);
                                      e.target.value = '';
                                   }
                                }}
                              />
                              <button 
                                 type="button"
                                 className="bg-slate-700 hover:bg-slate-600 px-2 py-1 rounded text-xs text-white"
                                 onClick={(e) => {
                                    const input = e.currentTarget.previousSibling;
                                    handleAddSubcategoria(c.id, input.value);
                                    input.value = '';
                                 }}
                              >
                                 +
                              </button>
                           </div>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'servicios' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Plataformas y Servicios</h3>
              <p className="text-slate-400 text-sm mb-6">Administra los servicios que vende la empresa (Ej: Netflix, Prime Video).</p>
              
              <form onSubmit={handleAddServicio} className="flex flex-col md:flex-row gap-4 items-end bg-slate-900 p-4 rounded-xl border border-slate-700">
                <div className="flex-1 w-full">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Nombre del Servicio</label>
                  <input type="text" value={nuevoServicio} onChange={(e) => setNuevoServicio(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" placeholder="Ej: Netflix" />
                </div>
                <div className="w-full md:w-48">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Categoría</label>
                  <select value={nuevoServicioTipo} onChange={(e) => setNuevoServicioTipo(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500">
                    <option value="Cuenta Completa">Cuenta Completa</option>
                    <option value="Cuenta para Perfiles">Cuenta para Perfiles</option>
                  </select>
                </div>
                <div className="w-full md:w-32">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Punto Reorden</label>
                  <input type="number" min="0" value={nuevoServicioMin} onChange={(e) => setNuevoServicioMin(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" />
                </div>
                <div className="w-full md:w-32">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Color</label>
                  <input type="color" value={nuevoServicioColor} onChange={(e) => setNuevoServicioColor(e.target.value)} className="w-full h-10 bg-slate-800 border border-slate-600 rounded-lg cursor-pointer" />
                </div>
                <button type="submit" className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                  <Plus size={18} /> Añadir
                </button>
              </form>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
              {/* Lista Cuentas Completas */}
              <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 bg-indigo-500/10 border-b border-slate-700">
                  <h4 className="font-bold text-indigo-400">Cuentas Completas</h4>
                </div>
                <ul className="divide-y divide-slate-700 max-h-64 overflow-y-auto">
                  {servicios.filter(s => (s.tipo_producto || 'Cuenta Completa') === 'Cuenta Completa').length === 0 ? (
                    <li className="p-4 text-sm text-slate-500 text-center">No hay servicios registrados</li>
                  ) : (
                    servicios.filter(s => (s.tipo_producto || 'Cuenta Completa') === 'Cuenta Completa').map(s => (
                      <li key={s.id} className="p-3 flex justify-between items-center hover:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: s.color || '#6366f1' }}></div>
                            <span className="text-slate-300 font-medium">{s.nombre}</span>
                            <span className="text-xs text-slate-500 ml-2 border border-slate-700 px-2 py-0.5 rounded">Alerta: {s.punto_de_reorden || 5}</span>
                        </div>
                        <button onClick={() => handleDeleteServicio(s.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={16} /></button>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              {/* Lista Cuentas para Perfiles */}
              <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 bg-cyan-500/10 border-b border-slate-700">
                  <h4 className="font-bold text-cyan-400">Cuentas para Perfiles</h4>
                </div>
                <ul className="divide-y divide-slate-700 max-h-64 overflow-y-auto">
                  {servicios.filter(s => s.tipo_producto === 'Cuenta para Perfiles').length === 0 ? (
                    <li className="p-4 text-sm text-slate-500 text-center">No hay servicios registrados</li>
                  ) : (
                    servicios.filter(s => s.tipo_producto === 'Cuenta para Perfiles').map(s => (
                      <li key={s.id} className="p-3 flex justify-between items-center hover:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: s.color || '#6366f1' }}></div>
                            <span className="text-slate-300 font-medium">{s.nombre}</span>
                            <span className="text-xs text-slate-500 ml-2 border border-slate-700 px-2 py-0.5 rounded">Alerta: {s.punto_de_reorden || 5}</span>
                        </div>
                        <button onClick={() => handleDeleteServicio(s.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={16} /></button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'socios' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Gestión de Socios</h3>
              <p className="text-slate-400 text-sm mb-6">Administra los socios y su porcentaje de participación en las ganancias.</p>
              
              <form onSubmit={handleAddSocio} className="flex flex-col md:flex-row gap-4 items-end bg-slate-900 p-4 rounded-xl border border-slate-700">
                <div className="flex-1 w-full">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Nombre del Socio</label>
                  <input type="text" value={nuevoSocio.nombre} onChange={(e) => setNuevoSocio({...nuevoSocio, nombre: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" placeholder="Ej: Juan Pérez" />
                </div>
                <div className="w-full md:w-32">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Participación (%)</label>
                  <input type="number" min="0" max="100" value={nuevoSocio.porcentaje} onChange={(e) => setNuevoSocio({...nuevoSocio, porcentaje: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" />
                </div>
                <div className="w-full md:w-32">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Estado</label>
                  <select value={nuevoSocio.estado} onChange={(e) => setNuevoSocio({...nuevoSocio, estado: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500">
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
                <button type="submit" className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                  <Plus size={18} /> Añadir
                </button>
              </form>
            </div>

            <div className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden w-full">
              <div className="p-4 bg-indigo-500/10 border-b border-slate-700">
                <h4 className="font-bold text-indigo-400">Socios Registrados</h4>
              </div>
              <ul className="divide-y divide-slate-700 max-h-64 overflow-y-auto">
                {socios.length === 0 ? (
                  <li className="p-4 text-sm text-slate-500 text-center">No hay socios registrados</li>
                ) : (
                  socios.map(s => (
                    <li key={s.id} className="p-4 flex justify-between items-center hover:bg-slate-800/50">
                      <div>
                        <span className="text-slate-300 font-bold">{s.nombre}</span>
                        <div className="text-xs text-slate-500 flex gap-2 mt-1">
                          <span className="bg-slate-800 px-2 py-0.5 rounded border border-slate-600">{s.porcentaje_participacion}%</span>
                          <span className={`px-2 py-0.5 rounded border uppercase text-[10px] font-bold ${s.estado === 'activo' ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-slate-500/10 border-slate-500/50 text-slate-400'}`}>{s.estado}</span>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteSocio(s.id)} className="text-slate-500 hover:text-red-400 p-2"><Trash2 size={18} /></button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        )}
        {activeTab === 'rutinas' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Gestión de Rutinas y Checklist</h3>
              <p className="text-slate-400 text-sm mb-6">Administra las tareas operativas diarias por departamento.</p>
              
              <form onSubmit={handleAddRutina} className="flex flex-col md:flex-row gap-4 items-end bg-slate-900 p-4 rounded-xl border border-slate-700">
                <div className="w-full md:w-48">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Departamento</label>
                  <select value={nuevaRutinaDep} onChange={(e) => setNuevaRutinaDep(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500">
                    <option value="Gerencia">Gerencia</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Cuentas">Gestión de Cuentas</option>
                    <option value="Atención al Cliente">Atención al Cliente</option>
                  </select>
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Tarea / Descripción</label>
                  <input type="text" value={nuevaRutinaTarea} onChange={(e) => setNuevaRutinaTarea(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" placeholder="Ej: Revisar correos de soporte" />
                </div>
                <button type="submit" className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                  <Plus size={18} /> Añadir
                </button>
              </form>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {['Gerencia', 'Marketing', 'Cuentas', 'Atención al Cliente'].map(dep => {
                const filtered = rutinas.filter(r => r.departamento === dep);
                return (
                  <div key={dep} className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
                    <div className="p-4 bg-indigo-500/10 border-b border-slate-700">
                      <h4 className="font-bold text-indigo-400">{dep}</h4>
                    </div>
                    <ul className="divide-y divide-slate-700 max-h-64 overflow-y-auto">
                      {filtered.length === 0 ? (
                        <li className="p-4 text-sm text-slate-500 text-center">No hay rutinas registradas</li>
                      ) : (
                        filtered.map((r, idx) => (
                          <li key={r.id} className="p-3 flex justify-between items-center hover:bg-slate-800/50">
                            <div className="flex items-center gap-2 max-w-[75%]">
                              <div className="flex items-center gap-1 shrink-0">
                                <GripVertical size={14} className="text-slate-500" />
                                <input 
                                  key={r.id + '-' + (r.orden || 0)}
                                  type="number" 
                                  defaultValue={r.orden || 0} 
                                  onBlur={async (e) => {
                                    const nuevoOrden = parseInt(e.target.value, 10);
                                    if (isNaN(nuevoOrden) || nuevoOrden === r.orden) return;
                                    try {
                                      await updateDoc(doc(db, 'config_rutinas', r.id), { orden: nuevoOrden });
                                    } catch (error) {
                                      console.error("Error al guardar orden: ", error);
                                      Swal.fire('Error', 'No se pudo guardar el orden', 'error');
                                    }
                                  }}
                                  className="w-11 bg-slate-800 border border-slate-700 rounded text-center text-xs text-white py-0.5 focus:border-indigo-500 focus:outline-none font-mono"
                                />
                              </div>
                              <span className="text-slate-300 text-xs leading-tight">
                                {r.tarea}
                              </span>
                            </div>
                            <button onClick={() => handleDeleteRutina(r.id)} className="text-slate-500 hover:text-red-400 p-1 ml-0.5"><Trash2 size={14} /></button>
                          </li>
                        ))

                      )}
                    </ul>
                  </div>
                );
              })}
            </div>

          </div>
        )}
      </div>


        {activeTab === 'auditoria' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
              <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">Historial de Auditoría</h3>
                  <p className="text-slate-400 text-sm">Registro de operaciones críticas y movimientos administrativos</p>
                </div>
                <div className="relative w-full md:w-64">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Filtrar logs..."
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-700">
                <table className="w-full text-sm text-left text-slate-300">
                  <thead className="bg-slate-900 text-slate-400 uppercase text-xs">
                    <tr>
                      <th className="px-6 py-4">Fecha</th>
                      <th className="px-6 py-4">Usuario</th>
                      <th className="px-6 py-4">Módulo</th>
                      <th className="px-6 py-4">Acción</th>
                      <th className="px-6 py-4">Descripción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {auditLogs
                      .filter(log => 
                        (log.nombreUsuario || '').toLowerCase().includes(auditSearch.toLowerCase()) ||
                        (log.modulo || '').toLowerCase().includes(auditSearch.toLowerCase()) ||
                        (log.accion || '').toLowerCase().includes(auditSearch.toLowerCase()) ||
                        (log.descripcion || '').toLowerCase().includes(auditSearch.toLowerCase())
                      )
                      .map(log => {
                        const date = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                        return (
                          <tr key={log.id} className="hover:bg-slate-800/30">
                            <td className="px-6 py-4 font-mono text-xs text-slate-400">
                              {log.timestamp ? date.toLocaleString('es-ES') : 'Pendiente'}
                            </td>
                            <td className="px-6 py-4 font-bold text-slate-200">
                              {log.nombreUsuario}
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-500/20 text-indigo-400">
                                {log.modulo}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-slate-300 font-medium">
                              {log.accion}
                            </td>
                            <td className="px-6 py-4 text-slate-400 text-xs">
                              {log.descripcion}
                            </td>
                          </tr>
                        );
                      })}
                    {auditLogs.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-slate-500 italic">No hay registros de auditoría disponibles</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      {showUserModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-5 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Nuevo Usuario</h3>
              <button onClick={() => setShowUserModal(false)} className="text-slate-400 hover:text-white">x</button>
            </div>
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">

              
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Nombre</label>
                <input required type="text" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Correo Electrónico</label>
                <input required type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Rol</label>
                <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-indigo-500">
                  <option value="ADMIN">ADMIN</option>
                  <option value="GERENTE">GERENTE</option>
                  <option value="ATENCIÓN AL CLIENTE">ATENCIÓN AL CLIENTE</option>
                  <option value="GESTIÓN DE CUENTAS">GESTIÓN DE CUENTAS</option>
                  <option value="MARKETING">MARKETING</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Contraseña Temporal</label>
                <input type="text" placeholder="Asigna una contraseña inicial" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-indigo-500" />
              </div>
              
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowUserModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors">Cancelar</button>
                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors">Crear Perfil</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configuracion;